import { ConnectorInfo } from '@blaze-shared/api-contracts-domain';
import { BaseSchema } from '@blaze-shared/schema-domain';
import { PivotWorkflowNode, WorkflowError } from '@blaze-shared/workflow-domain';

import {
  COLUMN_SOURCE_ID,
  FUNCTION_SOURCE_ID,
  GROUP_BY_SOURCE_ID,
  NODE_SOURCE_ID,
} from '../../errors/source-ids';
import { NodeNotLastWorkflowError } from '../../errors/workflow-error';
import {
  getColumnReferencesFromString,
  getParamReferencesFromString,
  getTagReferencesFromString,
} from '../../references/get-references-from-string';
import {
  WorkflowNodeColumnReference,
  WorkflowNodeNamedInputReference,
  WorkflowNodeParamReference,
  WorkflowNodeTagReference,
} from '../../references/references.models';
import { BaseWorkflowNode, WorkflowNode } from '../../registry/node.registry';

@WorkflowNode('Pivot')
export class PivotNode extends BaseWorkflowNode<PivotWorkflowNode> {
  static readonly COLUMN_SOURCE_ID = COLUMN_SOURCE_ID;
  static readonly FUNCTION_SOURCE_ID = FUNCTION_SOURCE_ID;
  static readonly GROUP_BY_SOURCE_ID = GROUP_BY_SOURCE_ID;
  static readonly NODE_SOURCE_ID = NODE_SOURCE_ID;

  getColumnReferences(schemas: Map<string | symbol, BaseSchema>): WorkflowNodeColumnReference[] {
    return [
      ...getColumnReferencesFromString(this.node.pivotColumn, schemas, PivotNode.COLUMN_SOURCE_ID),
      ...getColumnReferencesFromString(
        this.node.aggregateFunction.column,
        schemas,
        PivotNode.FUNCTION_SOURCE_ID,
      ),
      ...this.node.groupByColumns.flatMap((fn, i) =>
        getColumnReferencesFromString(fn, schemas, PivotNode.GROUP_BY_SOURCE_ID + i),
      ),
    ];
  }

  getTagReferences(schemas: Map<string | symbol, BaseSchema>): WorkflowNodeTagReference[] {
    return [
      ...getTagReferencesFromString(this.node.pivotColumn, schemas, PivotNode.COLUMN_SOURCE_ID),
      ...getTagReferencesFromString(
        this.node.aggregateFunction.column,
        schemas,
        PivotNode.FUNCTION_SOURCE_ID,
      ),
      ...this.node.groupByColumns.flatMap((fn, i) =>
        getTagReferencesFromString(fn, schemas, PivotNode.GROUP_BY_SOURCE_ID + i),
      ),
    ];
  }

  getParamReferences(
    schemas: Map<string | symbol, BaseSchema>,
    connectors: ConnectorInfo[],
  ): WorkflowNodeParamReference[] {
    return [
      ...getParamReferencesFromString(
        this.node.pivotColumn,
        schemas,
        connectors,
        PivotNode.COLUMN_SOURCE_ID,
      ),
      ...getParamReferencesFromString(
        this.node.aggregateFunction.column,
        schemas,
        connectors,
        PivotNode.FUNCTION_SOURCE_ID,
      ),
      ...this.node.groupByColumns.flatMap((fn, i) =>
        getParamReferencesFromString(fn, schemas, connectors, PivotNode.GROUP_BY_SOURCE_ID + i),
      ),
    ];
  }

  getNamedInputReferences(
    _schemas: Map<string | symbol, BaseSchema>,
  ): WorkflowNodeNamedInputReference[] {
    return [];
  }

  override validate(
    schemas: Map<string | symbol, BaseSchema>,
    connectors: ConnectorInfo[],
  ): WorkflowError<any>[] {
    const errors = super.validate(schemas, connectors);

    if (this.node.nextNodeId) {
      errors.push(
        new NodeNotLastWorkflowError(
          this.workflowId,
          this.nodeId,
          'Pivot',
          PivotNode.NODE_SOURCE_ID,
        ),
      );
    }

    return errors;
  }
}
